import { supabase } from '../lib/supabase'
import type { TeamMember } from '../types/portal'
import { resolveProjectId } from './projectWrites'

/**
 * PORTAL TEAM — public.project_staff is the ONLY source of truth.
 *
 * Internal staff assignments live here, deliberately separate from
 * public.project_members, which belongs to the client access system and is
 * never touched by this file.
 *
 * There is no fallback of any kind: no creator, no owner, no signed-in user,
 * no local default. A project with no rows has no team. Only the user_id and
 * the project-specific responsibility are stored — names always come from
 * public.staff_users.
 */

const TABLE = 'project_staff'

export interface StaffOption {
  userId: string
  name: string
  email: string
}

interface WriteResult {
  ok: boolean
  error?: string
}

/** The whole error object, so the cause is never guesswork. */
function log(what: string, error: unknown) {
  if (error) console.error(`[supabase] ${what} failed`, error)
}

/** The Portal staff who can be assigned. Read straight from staff_users. */
export async function fetchStaffDirectory(): Promise<StaffOption[]> {
  if (!supabase) return []

  const { data, error } = await supabase.from('staff_users').select('user_id, name, email')
  log('staff directory load', error)

  return (data ?? []).map((row: Record<string, unknown>) => {
    const email = String(row.email ?? '')
    return {
      userId: String(row.user_id ?? ''),
      name: String(row.name ?? '') || email.split('@')[0] || 'Portal staff',
      email,
    }
  })
}

/**
 * The saved assignments for one project, resolved against staff_users.
 * Returns an empty list when there are no rows — never a substitute.
 */
export async function fetchProjectTeam(projectId: string, slug: string): Promise<TeamMember[]> {
  if (!supabase) return []

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) return []

  const { data: rows, error } = await supabase
    .from(TABLE)
    .select('project_id, user_id, responsibility, created_at')
    .eq('project_id', realId)
    .order('created_at', { ascending: true })
  log('project staff load', error)

  if (!rows?.length) return []

  const directory = await fetchStaffDirectory()
  const byId = new Map(directory.map((person) => [person.userId, person]))

  return rows.map((row: Record<string, unknown>) => {
    const userId = String(row.user_id ?? '')
    const person = byId.get(userId)
    return {
      id: userId,
      name: person?.name ?? 'Portal staff',
      role: String(row.responsibility ?? '') || 'Crew',
    }
  })
}

/** Assigns one staff member. Nothing is shown until this succeeds. */
export async function assignProjectMember(
  projectId: string,
  slug: string,
  userId: string,
  responsibility: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'offline' }
  if (!userId) return { ok: false, error: 'missing user' }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) return { ok: false, error: 'project not found in the database' }

  /* (project_id, user_id) is unique, so a repeat assignment is simply kept. */
  const { error } = await supabase.from(TABLE).upsert(
    {
      project_id: realId,
      user_id: userId,
      responsibility: responsibility || 'Crew',
    },
    { onConflict: 'project_id,user_id' },
  )

  log('project staff insert', error)
  return error ? { ok: false, error: error.message } : { ok: true }
}

/** Changes the project-specific responsibility recorded against an assignment. */
export async function updateProjectMemberRole(
  projectId: string,
  slug: string,
  userId: string,
  responsibility: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'offline' }
  if (!userId) return { ok: false, error: 'missing user' }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) return { ok: false, error: 'project not found in the database' }

  const { error } = await supabase
    .from(TABLE)
    .update({ responsibility })
    .eq('project_id', realId)
    .eq('user_id', userId)

  log('project staff responsibility update', error)
  return error ? { ok: false, error: error.message } : { ok: true }
}

/**
 * Unassigns one staff member. Anyone can be removed — being the current user,
 * an owner, or the project's creator makes no difference.
 */
export async function removeProjectMember(
  projectId: string,
  slug: string,
  userId: string,
): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'offline' }
  if (!userId) return { ok: false, error: 'missing user' }

  const realId = await resolveProjectId(projectId, slug)
  if (!realId) return { ok: false, error: 'project not found in the database' }

  const { error, count } = await supabase
    .from(TABLE)
    .delete({ count: 'exact' })
    .eq('project_id', realId)
    .eq('user_id', userId)

  log('project staff delete', error)
  if (error) return { ok: false, error: error.message }

  /* A silent zero-row delete means RLS refused it without raising an error. */
  if (count === 0) {
    console.error(
      '[supabase] project staff delete removed no rows — the DELETE policy on project_staff is blocking it',
    )
    return { ok: false, error: 'delete refused by row-level security' }
  }

  return { ok: true }
}
